// 🔧 request_event_success.js
// ✅ Purpose: Public confirmation page after LGV Event Package purchase
// ✅ Features:
// - Receives Stripe Checkout return parameters
// - Shows purchase success confirmation
// - Explains that the event request is awaiting approval
// - Gives clear next-step buttons
// - Keeps payment confirmation separate from event approval
// - Uses standard LGV container/card layout
// - Fully block-commented for clarity and future devs

import React from "react";
import { Link, useLocation } from "react-router-dom";
import SeoHead from "../../components/seo_head";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";
import {
  buildPurchaseReceiptView,
} from "../../utils/build_purchase_receipt_view";
import { useEffect, useState } from "react";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

/*
=====================================================
Receipt Display Helpers

Purpose:
- Keeps browser receipt formatting consistent
- Converts stored cents into customer currency
=====================================================
*/

function formatMoney(amountCents = 0) {

  return `$${(
    Number(amountCents || 0) / 100
  ).toFixed(2)}`;

}

const RequestEventSuccess = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const orderId = params.get("orderId");
  const sessionId = params.get("session_id");
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {

    async function loadReceipt() {

      if (!orderId && !sessionId) {

        setLoading(false);
        return;

      }


      try {

        let orderSnap = null;


        /*
        =====================================================
        Attempt 1:
        Lookup by LGV order document ID
        =====================================================
        */

        if (orderId) {

          orderSnap =
            await getDoc(
              doc(
                db,
                "lgv_orders",
                orderId
              )
            );

        }



        /*
        =====================================================
        Attempt 2:
        Lookup by Stripe Checkout Session ID
        =====================================================
        */

        if (
          (!orderSnap || !orderSnap.exists()) &&
          sessionId
        ) {


          const querySnapshot =
            await getDocs(
              query(
                collection(
                  db,
                  "lgv_orders"
                ),
                where(
                  "stripeCheckoutSessionId",
                  "==",
                  sessionId
                )
              )
            );


          if (!querySnapshot.empty) {

            orderSnap =
              querySnapshot.docs[0];

          }

        }



        if (
          !orderSnap ||
          !orderSnap.exists()
        ) {

          console.warn(
            "LGV purchase order not found",
            {
              orderId,
              sessionId,
            }
          );

          setLoading(false);
          return;

        }



        const order = {

          id:
            orderSnap.id,

          ...orderSnap.data(),

        };


        console.log(
          "LGV receipt order loaded:",
          order
        );

        console.log(
          "LGV browser receipt model:",
          buildPurchaseReceiptView(order)
        );

        setReceipt(
          buildPurchaseReceiptView(order)
        );

        console.log(
          "FINAL RECEIPT VIEW MODEL",
          buildPurchaseReceiptView(order)
        );

      }

      catch(error) {

        console.error(
          "Failed loading purchase receipt:",
          error
        );

      }

      finally {

        setLoading(false);

      }

    }

    loadReceipt();

  }, [orderId]);

  return (
    <>
    <SeoHead
      title="Event Package Purchase Complete | Let's Go Vote"
      description="Your Let's Go Vote event package purchase has been completed."
      path="/request_event_success"
      noindex={true}
      breadcrumbs={[
        { name: "Home", path: "/" },
        {
          name: "Start Your Event",
          path: "/request_event",
        },
        {
          name: "Purchase Complete",
          path: "/request_event_success",
        },
      ]}
    />
      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="card shadow-sm border-0">

          <div className="card-body p-4 text-start">

            <h1 className="h3 fw-bold mb-3">
              Purchase Complete
            </h1>
            <p className="mb-4">
              Thank you for your Let's Go Vote purchase.
              Your payment has been received and your event
              is now active.
            </p>

            <div className="card bg-light border-0 mb-4">

              <div className="card-body">

                <h2 className="h5 fw-bold mb-4">
                  Purchase Receipt
                </h2>


                {receipt && (

                  <div>

                    {/* Event Package line item */}
                    <div className="d-flex justify-content-between border-bottom py-2">

                      <span>
                        {receipt.package?.name ||
                          "LGV Event Package"}
                      </span>

                      <strong>
                        {receipt.package?.amountDisplay ||
                          formatMoney(
                            receipt.package?.amountCents
                          )}
                      </strong>

                    </div>


                    {/* Power Ups */}
                    <div className="mt-4">

                      <h3 className="h6 fw-bold">
                        Power Ups
                      </h3>


                      {receipt.addons?.length > 0 ? (

                        <div className="d-flex flex-column gap-3 mt-3">

                          {receipt.addons.map(
                            (addon) => (

                              <div
                                key={addon.key}
                                className="border-bottom pb-3"
                              >

                                <div className="d-flex justify-content-between">

                                  <strong>
                                    {addon.name}
                                  </strong>


                                  <strong>
                                    {addon.amountDisplay ||
                                      formatMoney(
                                        addon.amountCents
                                      )}
                                  </strong>

                                </div>


                                {addon.configUrl && (

                                  <div className="mt-2">

                                    <a
                                      href={addon.configUrl}
                                      className="btn btn-sm btn-outline-primary"
                                    >
                                      Configure {addon.name}
                                    </a>

                                  </div>

                                )}

                              </div>

                            )
                          )}

                        </div>

                      ) : (

                        <div className="text-muted">
                          No Power Ups selected.
                        </div>

                      )}

                    </div>


                    {/* Total */}
                    <div className="d-flex justify-content-between border-top mt-4 pt-3">

                      <strong>
                        Total Paid
                      </strong>


                      <strong>
                        {receipt.total?.amountDisplay ||
                          formatMoney(
                            receipt.total?.amountCents
                          )}
                      </strong>

                    </div>


                    {/* Order information */}
                    <div className="small text-muted mt-4">

                      <div>
                        <strong>
                          Order ID:
                        </strong>{" "}
                        {receipt.orderId}
                      </div>


                      <div className="mt-1">

                        <strong>
                          Status:
                        </strong>{" "}
                        Event Active

                      </div>

                    </div>


                  </div>

                )}


                {loading && (

                  <div className="text-muted">
                    Loading purchase details...
                  </div>

                )}

              </div>

            </div>
            <h2 className="h5 fw-bold">
              What happens next?
            </h2>


            <p className="text-muted">

              Your event is ready.
              Continue setup by configuring your included
              Power Ups and event details.

            </p>



            <div className="card bg-light border-0 my-4">

              <div className="card-body">

                <h3 className="h6 fw-bold mb-3">
                  Event Setup Progress
                </h3>


                <div className="d-flex flex-column gap-2">

                  <div>
                    ✅ Payment received
                  </div>

                  <div>
                    ✅ Event created
                  </div>

                  <div>
                    ✅ Confirmation email sent
                  </div>

                  <div>
                    🚀 Ready for setup
                  </div>

                </div>

              </div>

            </div>



            <div className="d-flex flex-column flex-md-row gap-2">


              <Link
                to="/dashboard"
                className="btn btn-primary"
              >
                Go To Dashboard
              </Link>



              <Link
                to="/contact"
                className="btn btn-outline-secondary"
              >
                Contact Support
              </Link>



              <Link
                to="/"
                className="btn btn-outline-secondary"
              >
                Back To Home
              </Link>


            </div>


          </div>

          </div>
        </div>
      </section>
    </>
  );
};

export default RequestEventSuccess;